/**
 * useAttachableAspectCatalog.ts
 *
 * Lazy, on-demand fetch of the org's ATTACHABLE ASPECT catalog (marketplace
 * aspect definitions, via `listAspects`) — the registry `AspectsSection`'s
 * attach picker searches and every attached-aspect row resolves its display
 * name/definition against. Distinct from `useAspectCatalog` (the org-wide
 * SPHERE PROPERTY catalog TreeMap/Atlas use for filter-column binding) —
 * same "aspect" word, unrelated data shape, hence the longer name here.
 *
 * Lazy — not fetch-on-mount — because a type with no need to attach or draft
 * an aspect this session never pulls the org-wide list.
 *
 * Extracted from `AspectsSection` (EAINT-928 Phase 1) so the attach picker's
 * catalog state can be read by the composer root as well as the section that
 * owns the attached-aspects list: both `openAttach` and `openDraftAspect`
 * used to duplicate this fetch inline.
 */

import { useCallback, useRef, useState } from 'react';
import type { AspectRow } from 'types/aspect';
import type { ApiClients } from '../context/ApiClientsContext';
import { listAspects } from '../api/marketplaceApi';

export type AttachableAspectCatalogStatus = 'idle' | 'loading' | 'error';

export interface UseAttachableAspectCatalogResult {
  /** `null` until the fetch has resolved at least once. */
  catalog: AspectRow[] | null;
  status: AttachableAspectCatalogStatus;
  /** Fetches the catalog if it hasn't already loaded (or isn't already loading). Safe to call
   * on every "open the picker" / "open the draft dialog" action — it no-ops once cached. */
  ensureLoaded: () => void;
}

/**
 * @param apiClients - null skips any fetch (not ready yet).
 * @param tenantId - the org whose aspect catalog to list.
 */
export function useAttachableAspectCatalog(
  apiClients: ApiClients | null,
  tenantId: string | undefined
): UseAttachableAspectCatalogResult {
  const [catalog, setCatalog] = useState<AspectRow[] | null>(null);
  const [status, setStatus] = useState<AttachableAspectCatalogStatus>('idle');
  // Guards against a second fetch firing while the first is still in flight — `status` alone
  // would work too, but a ref keeps `ensureLoaded`'s identity stable across the loading window.
  const loadingRef = useRef(false);

  const ensureLoaded = useCallback(() => {
    if (catalog !== null || loadingRef.current) return;
    if (!apiClients?.schemaClient) return;
    loadingRef.current = true;
    setStatus('loading');
    listAspects(apiClients.schemaClient, tenantId)
      .then((result) => {
        setCatalog(result);
        setStatus('idle');
      })
      .catch(() => setStatus('error'))
      .finally(() => {
        loadingRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClients, tenantId, catalog]);

  return { catalog, status, ensureLoaded };
}
